import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './AppIcon';

const FilePreview = ({ file, className = '', showFileName = true, onClick = null }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  if (!file) {
    return (
      <div className={`flex items-center justify-center bg-secondary-100 rounded ${className}`}>
        <Icon name="FileX" size={24} className="text-secondary-400" />
      </div>
    );
  }

  const resolveBaseUrl = () => {
    const envCandidates = [];

    if (typeof import.meta !== 'undefined' && import.meta.env) {
      envCandidates.push(import.meta.env.VITE_API_BASE_URL);
      envCandidates.push(import.meta.env.VITE_NEXTJS_API_URL);
    }

    if (typeof process !== 'undefined' && process.env) {
      envCandidates.push(process.env.REACT_APP_API_BASE_URL);
      envCandidates.push(process.env.VITE_API_BASE_URL);
      envCandidates.push(process.env.VITE_NEXTJS_API_URL);
    }

    if (typeof window !== 'undefined') {
      envCandidates.push(window.__API_BASE_URL__);
      envCandidates.push(window.REACT_APP_API_BASE_URL);
      envCandidates.push(window.VITE_API_BASE_URL);
      envCandidates.push(window.location?.origin);
    }

    const baseCandidate = envCandidates.find(Boolean) || 'http://localhost:8000';
    return baseCandidate.endsWith('/api') ? baseCandidate.replace(/\/api$/, '') : baseCandidate;
  };

  const resolveFileUrl = (raw) => {
    if (!raw) return null;
    const isAbsolute = /^(?:[a-z][a-z\d+\-.]*:|\/\/)/i.test(raw);
    if (isAbsolute || raw.startsWith('data:') || raw.startsWith('blob:')) {
      return raw;
    }
    const baseUrl = resolveBaseUrl();
    return raw.startsWith('/') ? `${baseUrl}${raw}` : `${baseUrl}/${raw}`;
  };

  const fileUrl = resolveFileUrl(
    file.file_url ||
    file.file_path ||
    file.url ||
    file.path ||
    file.signed_url ||
    file.download_url
  );
  
  const fileName = file.file_name || file.name || file.filename || 'Unknown file';
  const mimeType = file.mime_type || file.type || file.content_type;

  // More detailed debug logging

  // Check if it's an image
  const isImage = mimeType?.startsWith('image/') || 
                  fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  // Check if it's a PDF
  const isPdf = mimeType === 'application/pdf' || 
                fileName?.endsWith('.pdf');

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  const handleClick = (e) => {
    // Prevent any default behavior that could submit forms or navigate
    try {
      e?.preventDefault?.();
      e?.stopPropagation?.();
    } catch (_) {}
    if (onClick) {
      onClick(file);
    } else if (fileUrl) {
      // Open in new tab safely without allowing the new tab to control the opener
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (isImage && !imageError) {
    return (
      <div 
        className={`relative overflow-hidden rounded cursor-pointer ${className}`}
        onClick={handleClick}
      >
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-secondary-100">
            <Icon name="Loader2" size={20} className="text-secondary-400 animate-spin" />
          </div>
        )}
        <img
          src={fileUrl}
          alt={fileName}
          className="w-full h-full object-cover"
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        {showFileName && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 truncate">
            {fileName}
          </div>
        )}
      </div>
    );
  }

  // For documents or failed images
  return (
    <div 
      className={`flex flex-col items-center justify-center bg-secondary-100 rounded cursor-pointer hover:bg-secondary-200 transition-colors ${className}`}
      onClick={handleClick}
    >
      <Icon 
        name={isPdf ? "FileText" : "File"} 
        size={32} 
        className="text-secondary-500 mb-2" 
      />
      {showFileName && (
        <div className="text-xs text-secondary-700 text-center px-2 truncate w-full">
          {fileName}
        </div>
      )}
      <div className="text-xs text-secondary-500 mt-1">
        {isPdf ? 'PDF Document' : 'Document'}
      </div>
    </div>
  );
};

// Modal component for full-size preview
export const FilePreviewModal = ({ file, isOpen, onClose }) => {
  if (!isOpen || !file) return null;

  const resolveBaseUrl = () => {
    if (typeof window !== 'undefined') {
      const fromWindow = window.__API_BASE_URL__ || window.REACT_APP_API_BASE_URL || window.VITE_API_BASE_URL;
      return fromWindow || window.location.origin;
    }
    if (typeof process !== 'undefined') {
      return process.env?.REACT_APP_API_BASE_URL || process.env?.VITE_API_BASE_URL || 'http://localhost:8000';
    }
    return 'http://localhost:8000';
  };

  const resolveFileUrl = (raw) => {
    if (!raw) return null;
    const isAbsolute = /^(?:[a-z][a-z\d+\-.]*:|\/\/)/i.test(raw);
    if (isAbsolute || raw.startsWith('data:') || raw.startsWith('blob:')) {
      return raw;
    }
    const baseUrl = resolveBaseUrl();
    return raw.startsWith('/') ? `${baseUrl}${raw}` : `${baseUrl}/${raw}`;
  };

  const modalUrl = resolveFileUrl(
    file.file_url ||
    file.file_path ||
    file.url ||
    file.path ||
    file.signed_url ||
    file.download_url
  );
  const fileName = file.file_name || file.name || file.filename || 'Unknown file';
  const mimeType = file.mime_type || file.type || file.content_type;
  const isImage = mimeType?.startsWith('image/') || 
                  fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  // Detect if it's a PDF
  const isPdf = fileName?.toLowerCase().endsWith('.pdf') || mimeType === 'application/pdf';

  const hasOpenedRef = useRef(false);

  useEffect(() => {
    hasOpenedRef.current = false;
  }, [isOpen, modalUrl]);

  const handleOpenInNewTab = useCallback(() => {
    if (modalUrl) {
      window.open(modalUrl, '_blank', 'noopener,noreferrer')
    }
  }, [modalUrl]);

  useEffect(() => {
    if (isOpen && modalUrl && isPdf && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      handleOpenInNewTab();
      onClose?.();
    }
  }, [isOpen, modalUrl, isPdf, handleOpenInNewTab, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="relative max-w-7xl w-full max-h-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="absolute top-6 right-6 flex items-center gap-3 z-10">
          {modalUrl && (
            <button
              onClick={handleOpenInNewTab}
              className="bg-white px-3 py-2 rounded-lg shadow-lg text-sm font-medium text-primary hover:bg-secondary-100"
            >
              Open in New Tab
            </button>
          )}
          <button
            onClick={onClose}
            className="bg-white rounded-full p-2 hover:bg-secondary-100 shadow-lg"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-xl w-full h-[90vh] overflow-hidden">
          {isImage ? (
            <img
              src={modalUrl}
              alt={fileName}
              className="w-full h-full object-contain"
            />
          ) : isPdf ? (
            <object
              data={modalUrl}
              type="application/pdf"
              className="w-full h-full"
            >
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <Icon name="FileText" size={64} className="text-secondary-400 mb-4" />
                <p className="text-lg text-text-primary mb-4">Unable to display PDF in browser</p>
                <a
                  href={modalUrl}
                  download
                  className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Download PDF
                </a>
              </div>
            </object>
          ) : (
            <iframe
              src={modalUrl}
              title={fileName}
              className="w-full h-full"
              frameBorder="0"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreview;
